import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Bug, Compass, GlobeHemisphereWest, Medal, PawPrint, Printer, Rocket, SealCheck } from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageGuide from "@/components/PageGuide";

const TOKEN_KEY = "np:reactor-token";

export function reactorToken() {
  try {
    let t = localStorage.getItem(TOKEN_KEY);
    if (!t) {
      t = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
      localStorage.setItem(TOKEN_KEY, t);
    }
    return t;
  } catch {
    return null;
  }
}

/** Fire-and-forget stamp collection for a guide page visit. */
export function stampGuide(guideKey) {
  const token = reactorToken();
  if (!token || !guideKey) return;
  http.post("/passport/stamps", { guide_key: guideKey, anon_token: token }).catch(() => {});
}

const BADGE_ICONS = {
  "first-steps": Compass, "explorer": BookOpen, "globe-trotter": GlobeHemisphereWest,
  "junior-naturalist": PawPrint, "young-scientist": Rocket, "nature-master": Medal,
};

function StampSlot({ label, collected, to, testid }) {
  const inner = (
    <div className="flex flex-col items-center gap-1.5" data-testid={testid}>
      <span className={`flex h-16 w-16 items-center justify-center rounded-full border-2 transition-all ${
        collected
          ? "border-[#166534] bg-[#F0FDF4] text-[#166534] shadow-sm rotate-[-6deg]"
          : "border-dashed border-black/20 text-black/25"
      }`}>
        {collected ? <SealCheck size={26} weight="fill" /> : <Bug size={22} weight="light" />}
      </span>
      <span className={`text-center text-[11px] leading-tight max-w-[88px] ${collected ? "text-[#14532D] font-medium" : "text-black/40"}`}>
        {label}
      </span>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function NaturePassport() {
  const [data, setData] = useState(null);
  const [pillars, setPillars] = useState([]);

  useEffect(() => {
    http.get("/passport", { params: { token: reactorToken() } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ stamps: [], badges: [], progress: { species: 0, species_total: 8, science: 0, science_total: 12, total: 0, total_possible: 20 }, all_badges: [] }));
    http.get("/science/taxonomy").then(({ data }) => setPillars(data.pillars || [])).catch(() => {});
  }, []);

  const collected = useMemo(() => new Set((data?.stamps || []).map((s) => s.guide_key)), [data]);
  const earnedIds = useMemo(() => new Set((data?.badges || []).map((b) => b.id)), [data]);
  const p = data?.progress || { species: 0, species_total: 8, science: 0, science_total: 12, total: 0, total_possible: 20 };

  const GROUPS = [
    ["mammals", "Mammals"], ["birds", "Birds"], ["insects", "Insects & Arachnids"], ["reptiles", "Reptiles"],
    ["amphibians", "Amphibians"], ["fish-marine", "Fish & Marine Life"], ["fungi", "Fungi"], ["other", "Other Organisms"],
  ];

  return (
    <main className="min-h-screen bg-white text-[#14532D] pt-32 md:pt-36 pb-24" data-testid="nature-passport">
      <SeoMeta
        title="My Nature Passport — NaturePulse"
        description="Collect a stamp for every field guide you explore and earn badges — from First Steps to Nature Master."
        canonical={`${window.location.origin}/passport`}
      />
      <div className="max-w-[1000px] mx-auto px-5 md:px-10">
        <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Nature Passport" }]} />

        <div className="mt-6 rounded-3xl border-2 border-[#166534]/20 bg-gradient-to-b from-[#F0FDF4] to-white p-6 md:p-8">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212]">NaturePulse · Explorer document</div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl text-[#166534]" data-testid="passport-title">My Nature Passport</h1>
          <p className="mt-2 max-w-2xl text-base text-[#374151]">
            Every field guide you explore earns you a stamp. Fill the passport to earn every badge — no account needed, it lives on this device.
          </p>
          <a href={`${API_BASE}/api/passport/card.pdf?token=${reactorToken()}`} target="_blank" rel="noreferrer"
            data-testid="passport-print-card"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D] transition-colors">
            <Printer size={13} weight="bold" /> Print my passport card
          </a>
          <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="passport-progress">
            {[
              ["Species guides", p.species, p.species_total],
              ["Science guides", p.science, p.science_total],
              ["All stamps", p.total, p.total_possible],
            ].map(([label, n, total]) => (
              <div key={label} className="rounded-2xl border border-black/5 bg-white p-4">
                <div className="text-sm text-[#4B5563]">{label}</div>
                <div className="mt-1 font-serif-display text-2xl text-[#166534]" data-testid={`passport-count-${label.toLowerCase().replace(/\s+/g, "-")}`}>{n} / {total}</div>
                <div className="mt-2 h-2 rounded-full bg-black/5 overflow-hidden">
                  <div className="h-full rounded-full bg-[#166534] transition-all" style={{ width: `${total ? Math.round((n / total) * 100) : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Badge shelf */}
        <section className="mt-8" data-testid="passport-badges">
          <h2 className="font-serif-display text-2xl text-[#14532D]">Badge shelf</h2>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {(data?.all_badges || []).map((b) => {
              const Icon = BADGE_ICONS[b.id] || Medal;
              const earned = earnedIds.has(b.id);
              return (
                <div key={b.id} data-testid={`passport-badge-${b.id}`}
                  className={`rounded-2xl border p-4 text-center ${earned ? "border-[#B45309]/30 bg-[#FFFBEB]" : "border-black/10 bg-white opacity-60"}`}>
                  <Icon size={28} weight={earned ? "fill" : "light"} className={earned ? "mx-auto text-[#B45309]" : "mx-auto text-black/30"} />
                  <div className={`mt-2 text-sm font-semibold ${earned ? "text-[#78350F]" : "text-black/40"}`}>{b.label}</div>
                  <div className="mt-1 text-[11px] leading-tight text-[#6B7280]">{b.rule}</div>
                  {earned && <div className="mt-1.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#B45309]">Earned</div>}
                </div>
              );
            })}
          </div>
        </section>

        {/* Species stamps */}
        <section className="mt-10" data-testid="passport-species-stamps">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-serif-display text-2xl text-[#14532D]">SpeciesPulse field guides</h2>
            <Link to="/speciespulse" className="text-sm text-[#166534] underline underline-offset-2" data-testid="passport-explore-species">Explore species guides →</Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            {GROUPS.map(([slug, label]) => (
              <StampSlot key={slug} label={label} collected={collected.has(`species:group:${slug}`)}
                to={`/speciespulse/group/${slug}`} testid={`passport-stamp-species-${slug}`} />
            ))}
          </div>
        </section>

        {/* Science stamps */}
        <section className="mt-10" data-testid="passport-science-stamps">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-serif-display text-2xl text-[#14532D]">Science Pulse pillar guides</h2>
            <Link to="/science" className="text-sm text-[#166534] underline underline-offset-2" data-testid="passport-explore-science">Explore science pillars →</Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            {pillars.map((pl) => (
              <StampSlot key={pl.slug} label={pl.label} collected={collected.has(`science:pillar:${pl.slug}`)}
                to={`/science/${pl.slug}`} testid={`passport-stamp-science-${pl.slug}`} />
            ))}
          </div>
        </section>

        <div className="mt-10"><PageGuide pageKey="species:home" testidPrefix="passport" /></div>
      </div>
    </main>
  );
}
